/**
 * Utilidad para generar Attestation HMAC-SHA256
 *
 * El Attestation es una firma criptográfica HMAC-SHA256 que valida la autenticidad
 * de la petición y previene manipulación de datos.
 *
 * Formato del mensaje a firmar: {sessionToken}:{timestamp}:{nonce}:{sessionToken}
 * Clave secreta: CLIENT_SECRET (debe estar en .env)
 *
 * Características:
 * - Usa HMAC-SHA256 para firma criptográfica
 * - Incluye sessionToken, timestamp y nonce en el mensaje
 * - Requiere CLIENT_SECRET compartido con el backend
 * - El backend valida la firma con la misma clave
 */

/**
 * Convierte un string a ArrayBuffer para usar con SubtleCrypto
 * @param {string} str - String a convertir
 * @returns {ArrayBuffer} ArrayBuffer con los bytes del string
 */
const stringToArrayBuffer = (str) => {
  const encoder = new TextEncoder()
  return encoder.encode(str)
}

/**
 * Convierte un ArrayBuffer a string hexadecimal
 * @param {ArrayBuffer} buffer - Buffer a convertir
 * @returns {string} String hexadecimal
 */
const arrayBufferToHex = (buffer) => {
  const byteArray = new Uint8Array(buffer)
  return Array.from(byteArray, (byte) => byte.toString(16).padStart(2, '0')).join('')
}

/**
 * Calcula HMAC-SHA256 usando Web Crypto API
 *
 * @param {string} message - Mensaje a firmar
 * @param {string} secret - Clave secreta (CLIENT_SECRET)
 * @returns {Promise<string>} Firma HMAC-SHA256 en hexadecimal
 */
const calculateHMAC = async (message, secret) => {
  try {
    // Importar la clave secreta
    const key = await crypto.subtle.importKey('raw', stringToArrayBuffer(secret), { name: 'HMAC', hash: 'SHA-256' }, false, [
      'sign',
    ])

    // Calcular HMAC
    const signature = await crypto.subtle.sign('HMAC', key, stringToArrayBuffer(message))

    // Convertir a hexadecimal
    return arrayBufferToHex(signature)
  } catch (error) {
    console.error('[Attestation] Error calculating HMAC:', error)
    throw new Error('Failed to calculate attestation signature')
  }
}

/**
 * Genera el Attestation HMAC-SHA256 para una petición
 *
 * Formato del mensaje: {sessionToken}:{timestamp}:{nonce}:{sessionToken}
 *
 * @param {string} sessionToken - Token de sesión actual
 * @param {string} timestamp - Timestamp Unix en segundos (string)
 * @param {string} nonce - Request Nonce único
 * @param {string} clientSecret - CLIENT_SECRET desde .env
 * @returns {Promise<string>} Attestation HMAC-SHA256 en hexadecimal
 */
export const generateAttestation = async (sessionToken, timestamp, nonce, clientSecret) => {
  // Validar parámetros
  if (!sessionToken || typeof sessionToken !== 'string') {
    throw new Error('Invalid sessionToken: must be a non-empty string')
  }

  if (!timestamp || typeof timestamp !== 'string') {
    throw new Error('Invalid timestamp: must be a non-empty string')
  }

  if (!nonce || typeof nonce !== 'string') {
    throw new Error('Invalid nonce: must be a non-empty string')
  }

  if (!clientSecret || typeof clientSecret !== 'string') {
    throw new Error('Invalid clientSecret: must be a non-empty string')
  }

  // Construir mensaje según formato del backend
  // Formato: {sessionToken}:{timestamp}:{nonce}:{sessionToken}
  const message = `${sessionToken}:${timestamp}:${nonce}:${sessionToken}`

  // Calcular HMAC-SHA256
  const attestation = await calculateHMAC(message, clientSecret)

  return attestation
}

/**
 * Valida el formato de un Attestation (solo validación de formato, no de firma)
 *
 * @param {string} attestation - Attestation a validar
 * @returns {boolean} true si el formato es válido (64 caracteres hexadecimales)
 */
export const isValidAttestationFormat = (attestation) => {
  if (!attestation || typeof attestation !== 'string') {
    return false
  }

  // HMAC-SHA256 produce 64 caracteres hexadecimales (32 bytes)
  return /^[a-f0-9]{64}$/i.test(attestation)
}

/**
 * Obtiene el CLIENT_SECRET desde las variables de entorno
 *
 * @returns {string|null} CLIENT_SECRET o null si no está configurado
 */
export const getClientSecret = () => {
  // En React, las variables de entorno deben empezar con REACT_APP_
  const secret = process.env.REACT_APP_CLIENT_SECRET

  if (!secret) {
    console.error('[Attestation] CLIENT_SECRET not found in environment variables')
    console.error('[Attestation] Make sure REACT_APP_CLIENT_SECRET is set in .env file')
    return null
  }

  return secret
}

/**
 * Verifica si el entorno está configurado correctamente para generar attestations
 *
 * @returns {boolean} true si CLIENT_SECRET está configurado
 */
export const isAttestationConfigured = () => {
  return getClientSecret() !== null
}
